import { Button, Typography } from "@povio/ui";
import Image from "next/image";
import { useState } from "react";

import { DownloadIcon } from "@/assets/icons/general/Download";
import { PlusSquaredIcon } from "@/assets/icons/general/PlusSquaredIcon";
import LossdogScoreSectionImage from "@/assets/images/home/jobfit-bg@2x.png";
import BackgroundImage from "@/assets/images/project/bg.png";
import LaptopImage from "@/assets/images/project/laptop.png";
import { ProjectFormModal } from "@/components/home/modals/ProjectFormModal";
import ProfileProjectMediaItem from "@/components/home/sections/components/ProfileProjectMediaItem";
import EmptyProjectSection from "@/components/home/sections/EmptyProjectSection";
import type { ISectionProps } from "@/components/home/sections/ProfileBioSection";
import ProfileProjectCard from "@/components/home/sections/ProfileProjectCard";
import ProfileSectionCard from "@/components/home/sections/ProfileSectionCard";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";
import { TourSelectors } from "@/config/tour.config";
import type { CommonModels } from "@/data/common/common.models";
import { useShowMore } from "@/hooks/useShowMore";
import { DemoContext } from "@/providers/demo/DemoContext";

interface IProps extends ISectionProps {
  projects: CommonModels.ProjectWithMediaDto[];
  featuredProject?: CommonModels.ProjectWithMediaDto | null;
  onProjectSaved?: () => void;
}

const ProfileProjectsSection = ({
  projects: allProjects,
  featuredProject,
  isPublic = false,
  onProjectSaved,
}: IProps) => {
  const { isDemoMode } = DemoContext.useDemo();

  const { ref: textRef, showMore } = useShowMore("line-clamp");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<CommonModels.ProjectWithMediaDto | null>(null);

  const projects = allProjects?.filter((project) => !featuredProject || project?.id !== featuredProject?.id);

  const hasPdf = featuredProject?.media?.find((media) => media.mimeType.includes("application/pdf")) || null;

  const onAddProject = () => {
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  const handleOnProjectClick = (project: CommonModels.ProjectWithMediaDto | null) => {
    if (!project) {
      return;
    }

    setSelectedProject(project);

    setIsModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  if (!featuredProject && !projects?.length) {
    return (
      <EmptyProjectSection
        onAddProject={onAddProject}
        isModalOpen={isModalOpen}
        closeProjectModal={closeProjectModal}
        onProjectSaved={onProjectSaved}
      />
    );
  }

  return (
    <ProfileSectionCard
      title="Featured Project"
      titleSize="title-3"
      isPublic={isPublic}
      containerClassName="flex flex-col gap-4"
      onEditSectionClicked={() => handleOnProjectClick(featuredProject || null)}
      titleId={TourSelectors.ProjectsSection}
    >
      <div className="flex dd:flex-row flex-col items-start justify-start gap-6">
        {!!featuredProject?.media?.[0] && (
          <div className="group relative h-[342px] dd:max-h-[500px] dd:w-1/2 dm:w-full dd:min-w-1/2 dd:overflow-hidden dd:rounded-button-rounding">
            <ProfileProjectMediaItem
              isCoverImage
              project={featuredProject as CommonModels.ProjectWithMediaDto}
            />
          </div>
        )}

        {featuredProject && !featuredProject.media?.[0] && !isPublic && (
          <div
            className="flex dd:h-[360px] dd:w-1/2 w-full flex-col items-center justify-center gap-4 rounded-sm bg-cover bg-no-repeat dd:p-0 p-4"
            style={{ backgroundImage: `url(${BackgroundImage.src})` }}
          >
            <div className="flex flex-row flex-wrap items-end justify-center gap-4">
              <Image
                src={LaptopImage}
                alt="Laptop"
                width={290}
                height={162}
                className="h-auto max-h-[160px] w-auto object-contain"
              />
              <div className="flex max-w-[223px] m:-translate-y-4 flex-col items-center justify-center gap-4">
                <Typography
                  size="body-1"
                  variant="prominent-2"
                  className="m:text-left text-center text-secondary-75 leading-6"
                >
                  Your <span className="text-primary">Project</span> cover image is currently empty.
                </Typography>
                <DemoClickWrapper>
                  <Button
                    color="secondary"
                    size="s"
                    isDisabled={isDemoMode}
                    onPress={() => handleOnProjectClick(featuredProject as CommonModels.ProjectWithMediaDto)}
                    className="w-full px-[24px]! py-[9px]! text-label-1!"
                  >
                    Upload Your Projects
                  </Button>
                </DemoClickWrapper>
              </div>
            </div>
          </div>
        )}

        {!featuredProject && (
          <div
            className="flex dd:h-[327px] dd:w-1/2 w-full flex-col items-center justify-center gap-4 rounded-s bg-cover bg-no-repeat dd:p-0 p-4"
            style={{ backgroundImage: `url(${LossdogScoreSectionImage.src})` }}
          >
            <Typography
              size="label-1"
              variant="prominent-1"
              className="text-center text-text-default-3"
            >
              Your Featured Project
              <br />
              content is currently empty.
            </Typography>
            <Button
              isDisabled={isDemoMode}
              color="secondary"
              data-testid="introduction-save-button"
              onPress={onAddProject}
            >
              Upload Your Featured Project
            </Button>
          </div>
        )}
        <div className="flex dd:w-1/2 w-full flex-col items-start justify-between gap-4 self-stretch">
          <div>
            <Typography
              size="title-3"
              className="mb-4"
              data-testid="featured-project-title"
            >
              {featuredProject?.title}
            </Typography>
            <Typography
              ref={textRef}
              variant="default"
              size="body-4"
              className="line-clamp-4 text-text-default-2"
              data-testid="featured-project-description"
            >
              {featuredProject?.description}
            </Typography>
            {showMore && (
              <div>
                <Button
                  variant="text"
                  size="none"
                  onPress={() => setIsShowMoreOpen(true)}
                >
                  Show More
                </Button>
              </div>
            )}
          </div>
          {hasPdf && (
            <Button
              variant="text"
              size="none"
              color="secondary"
              icon={DownloadIcon}
              onPress={() => {
                if (hasPdf?.url) {
                  window.open(hasPdf.url, "_blank");
                }
              }}
            >
              Download Project
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6 border-elevation-outline-default-2 border-t pt-6">
        {!!projects?.length && (
          <div className="relative w-full">
            <div className="scrollbar-hide flex w-full flex-1 snap-x snap-mandatory overflow-x-auto scroll-smooth">
              <div className="print-project-list flex grow gap-2">
                {projects?.map((project, index) => (
                  <ProfileProjectCard
                    key={project.id || index}
                    project={project}
                    isPublic={isPublic}
                    onProjectClick={handleOnProjectClick}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="t:w-auto w-full">
          <DemoClickWrapper>
            <Button
              color="secondary"
              size="s"
              className="t:w-auto w-full"
              icon={PlusSquaredIcon}
              onPress={onAddProject}
            >
              Add new project
            </Button>
          </DemoClickWrapper>
        </div>
      </div>

      {featuredProject && (
        <ModalSheet
          title={featuredProject.title}
          isOpen={isShowMoreOpen}
          onClose={() => setIsShowMoreOpen(false)}
        >
          <Typography
            variant="default"
            size="body-4"
            className="text-text-default-2"
          >
            {featuredProject.description}
          </Typography>
        </ModalSheet>
      )}

      {isModalOpen && (
        <ProjectFormModal
          key={selectedProject?.id}
          isOpen={isModalOpen}
          onClose={closeProjectModal}
          project={selectedProject || undefined}
          isFirstProject={!featuredProject && !projects?.length}
          onProjectSaved={onProjectSaved}
        />
      )}
    </ProfileSectionCard>
  );
};

export default ProfileProjectsSection;
